import { ReactNode } from "react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import type { LucideIcon } from "lucide-react"

export interface BaseInlineInputProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  hideLabel?: boolean
  fixedLabelWidth?: string
  size?: "sm" | "base" | "lg"
  className?: string
  children?: ReactNode
  alignLabel?: "center" | "start"
}

export function BaseInlineInput({
  label,
  LabelIcon,
  hideLabel = false,
  fixedLabelWidth = "120px",
  size,
  className,
  children,
  alignLabel = "center",
}: BaseInlineInputProps) {
  const shouldShowLabelArea = !hideLabel || (hideLabel && !!LabelIcon)
  const isIconOnly = hideLabel && !!LabelIcon
  const gridTemplateColumns = shouldShowLabelArea ? (isIconOnly ? "16px 1fr" : `${fixedLabelWidth} 1fr`) : "1fr"

  const renderLabelArea = () => {
    if (!hideLabel) {
      return (
        <div className={cn("inline-flex items-center", LabelIcon && "gap-2", alignLabel === "start" && "pt-1.5")}>
          {!!LabelIcon && (
            <LabelIcon className={cn("text-icon size-3.5", size === "lg" && "size-4")} strokeWidth={1.5} />
          )}
          <span className={cn("text-muted-foreground truncate text-xs", size === "lg" && "text-base")}>{label}</span>
        </div>
      )
    }

    if (hideLabel && !!LabelIcon) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex w-full cursor-help justify-center">
                <LabelIcon className="text-icon size-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={6} align="center">
              {label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return null
  }

  return (
    <div
      className={cn("grid min-h-8 w-full gap-2", alignLabel === "center" ? "items-center" : "items-start", className)}
      style={{ gridTemplateColumns }}
    >
      {shouldShowLabelArea && renderLabelArea()}
      <div className={cn("flex min-w-0", alignLabel === "center" && "items-center")}>{children}</div>
    </div>
  )
}
